import { defineStore } from 'pinia'
import {
  ContextSwitchResponseSchema,
  type ContextSwitchRequest,
  type ContextSwitchResponse,
  type RoleType,
  type SubscriberPickerItem,
  type UserAuth,
  type UserContext,
  type UserPermissions,
  type UserRole,
} from '~/schemas/auth'

interface UserRoleState {
  currentRole: UserRole | null
  availableContexts: UserContext[]
  permissions: UserPermissions | null
  platformSubscriberContext: number | null
  availableSubscribers: SubscriberPickerItem[]
}

export const useUserRoleStore = defineStore('userRole', {
  state: (): UserRoleState => ({
    currentRole: null,
    availableContexts: [],
    permissions: null,
    platformSubscriberContext: null,
    availableSubscribers: [],
  }),

  getters: {
    // Get user from nuxt-auth-sanctum
    user(): UserAuth | null {
      const { user } = useSanctumAuth<UserAuth>()
      return user.value || null
    },

    currentContext(): UserContext | null {
      if (!this.currentRole) return null

      return (
        this.availableContexts.find((ctx) => ctx.id === this.currentRole?.id) ||
        null
      )
    },

    hasMultipleRoles(): boolean {
      return this.availableContexts.length > 1
    },

    isAgencyAdmin(): boolean {
      return this.permissions?.is_agency_admin || false
    },

    isPlatformAdmin(): boolean {
      return this.permissions?.is_platform_admin || false
    },

    isSuperAdmin(): boolean {
      return this.permissions?.is_super_admin || false
    },

    isInSubscriberContext(): boolean {
      return this.isPlatformAdmin && this.platformSubscriberContext !== null
    },

    activeSubscriberId(): number | null {
      if (this.currentRole?.subscriber_id) {
        return this.currentRole.subscriber_id
      }
      if (this.platformSubscriberContext) {
        return this.platformSubscriberContext
      }
      return null
    },

    /**
     * Deduplicated list of subscriber contexts from availableContexts.
     * Picks the highest-privilege role per subscriber.
     */
    subscriberContexts(): UserContext[] {
      const roleHierarchy: Record<string, number> = {
        'agency-admin': 2,
        'agency-user': 1,
      }
      const map = new Map<number, UserContext>()

      for (const ctx of this.availableContexts) {
        if (!ctx.subscriber_id) continue
        const existing = map.get(ctx.subscriber_id)
        if (!existing || (roleHierarchy[ctx.role] ?? 0) > (roleHierarchy[existing.role] ?? 0)) {
          map.set(ctx.subscriber_id, ctx)
        }
      }

      return Array.from(map.values())
    },

    canManageClients(): boolean {
      return this.permissions?.can_manage_clients || false
    },

    canCreateClients(): boolean {
      return this.permissions?.can_create_clients || false
    },
    canInviteUsers(): boolean {
      return this.permissions?.can_invite_users || false
    },
    canAccessClient:
      (state) =>
      (clientId: number): boolean => {
        if (!state.permissions) return false

        const { accessible_client_ids, is_platform_admin } = state.permissions

        if (is_platform_admin || accessible_client_ids.includes('*')) {
          return true
        }

        // Check if has access to all clients of a subscriber
        const subscriberAccess = accessible_client_ids.find(
          (id) => typeof id === 'string' && id.startsWith('subscriber:')
        )

        if (subscriberAccess) {
          // Would need to check if client belongs to this subscriber
          // For now, we'll need to fetch this info
          return true
        }

        return accessible_client_ids.includes(clientId)
      },

    currentRoleName(): string {
      const roleMap: Record<RoleType, string> = {
        'super-admin': 'Super Admin',
        'platform-admin': 'Platform Admin',
        'agency-admin': 'Administrator agencije',
        'agency-user': 'Korisnik agencije',
      }

      return this.currentRole ? roleMap[this.currentRole.role] : 'Korisnik'
    },
  },

  actions: {
    // Initialize from auth user
    initializeFromAuth(): void {
      const { user } = useSanctumAuth<UserAuth>()

      if (user.value && 'current_role' in user.value) {
        this.currentRole = user.value.current_role || null
        this.availableContexts = user.value.available_contexts || []
        this.permissions = user.value.permissions || null
        this.platformSubscriberContext = user.value.platform_subscriber_context ?? null
        this.availableSubscribers = user.value.available_subscribers || []
      }
    },

    async switchContext(request: ContextSwitchRequest): Promise<void> {
      const { fetchSingle } = useApiResponse()
      await fetchSingle<ContextSwitchResponse>(
        '/api/user/switch-context',
        ContextSwitchResponseSchema,
        {
          method: 'POST',
          body: request,
        }
      )
    },

    async exitSubscriberContext(): Promise<void> {
      await this.switchContext({ subscriber_id: null })
    },

    reset(): void {
      this.currentRole = null
      this.availableContexts = []
      this.permissions = null
      this.platformSubscriberContext = null
      this.availableSubscribers = []
    },
  },
})
